import React from 'react';
import { Label } from '@/components/ui/label';
import { cn } from '@/lib/utils';

interface SettingRowProps {
  htmlFor?: string;
  label: string;
  description?: string;
  control: React.ReactNode;
  /** Mutes the label: `peer-disabled:*` needs the control to PRECEDE it. */
  disabled?: boolean;
  className?: string;
}

/**
 * Label and optional description on the left, control on a fixed right rail so
 * every switch and select on the page terminates at the same edge. Stacks below
 * the section's container width, not the viewport's — the chat panel resizes
 * this column independently of the window.
 */
const SettingRow: React.FC<SettingRowProps> = ({
  htmlFor,
  label,
  description,
  control,
  disabled,
  className,
}) => (
  <div
    className={cn(
      'flex flex-col gap-2 px-4',
      '@lg/section:flex-row @lg/section:justify-between @lg/section:gap-6',
      description
        ? 'min-h-[64px] py-3 @lg/section:items-start'
        : 'min-h-[52px] py-2 @lg/section:items-center',
      className,
    )}
  >
    <div className="flex min-w-0 flex-col gap-0.5">
      {/* Label ships leading-none; kanji needs 1.5 as a floor. */}
      <Label
        htmlFor={htmlFor}
        className={cn(
          'revy-cjk text-sm font-medium leading-[1.5]',
          disabled && 'cursor-not-allowed opacity-70',
        )}
      >
        {label}
      </Label>
      {description && (
        <p className="revy-cjk max-w-[40em] text-xs leading-[1.7] tracking-[0.02em] text-muted-foreground">
          {description}
        </p>
      )}
    </div>

    <div className="flex shrink-0 items-center justify-end gap-2 @lg/section:w-56 [&>button[role=combobox]]:w-full">
      {control}
    </div>
  </div>
);

export default SettingRow;
